import {
  TrashIcon,
  CloudArrowUpIcon,
  ArrowLeftStartOnRectangleIcon,
} from '@heroicons/react/24/solid';
import {
  Position,
  useStore,
  NodeProps,
  NodeToolbar,
  NodeResizer,
  useReactFlow,
  OnResizeStart,
  useUpdateNodeInternals,
} from '@xyflow/react';
import React, { memo, useRef, useState, useCallback } from 'react';
import { Button } from 'src/components/button';
import { FlipHIcon } from 'src/components/icons/FlipHIcon';
import { FlipVIcon } from 'src/components/icons/FlipVIcon';
import { RotateRightIcon } from 'src/components/icons/RotateRightIcon';
import { appConfig } from 'src/config';
import { useUndoRedo } from 'src/context/undo-redo';
import useDetachNodes from 'src/hooks/useDetachNodes';
import {
  mirrorBase64Image,
  rotateBase64Image,
  getWidthAndHeightFromBase64,
} from 'src/utils/image.utils';
import { twMerge } from 'tailwind-merge';

import './image-node.scss';
import { IImageNodeCategory } from './type';

const lineStyle = { borderColor: 'transparent' };

function ImageNode({
  id,
  data,
  positionAbsoluteX,
  positionAbsoluteY,
  width,
  height,
  selected,
  ...rest
}: NodeProps) {
  const { category } = data as { category: IImageNodeCategory };
  const [isRotating, setIsRotating] = useState(false);

  const { deleteElements } = useReactFlow();
  const { setNodes } = useReactFlow();
  const inputRef = useRef<HTMLInputElement>(null);
  const updateNodeInternals = useUpdateNodeInternals();
  const { takeSnapshot } = useUndoRedo();

  const onDelete = () => {
    deleteElements({ nodes: [{ id }] });
  };

  const hasParent = useStore((store) => !!store.nodeLookup.get(id)?.parentId);
  const detachNodes = useDetachNodes();

  const onDetach = () => detachNodes([id]);

  const onUpload = () => {
    const file = inputRef.current?.files?.[0];
    if (file) {
      const reader = new FileReader();
      // get width and height from file
      reader.onload = async (e) => {
        if (file.size > appConfig.imageSizeLimit) {
          alert(
            `Image size is too large. The maximum size is ${
              appConfig.imageSizeLimit / 1024 / 1024
            }MB`,
          );
          return;
        }

        const imageSize = await getWidthAndHeightFromBase64(
          e.target?.result as string,
        );

        // scale width and height to 100% of the image size
        const scale = Math.min(
          1,
          Math.min(
            (width ?? 0) / imageSize.width,
            (height ?? 0) / imageSize.height,
          ),
        );
        const newWidth = imageSize.width * scale;
        const newHeight = imageSize.height * scale;

        takeSnapshot();
        setNodes((nds) =>
          nds.map((node: any) =>
            node.id === id
              ? {
                  ...node,
                  width: newWidth,
                  height: newHeight,
                  data: {
                    ...node.data,
                    category: {
                      ...node.data?.category,
                      imageUrl: e.target?.result as string,
                    },
                  },
                }
              : node,
          ),
        );
        inputRef.current!.value = '';
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRotate = async () => {
    takeSnapshot();
    try {
      setIsRotating(true);
      const newImageUrl = category.imageUrl
        ? await rotateBase64Image(category.imageUrl, 90)
        : '';

      // calculate the new position
      const newPositionX =
        positionAbsoluteX + (width ?? 0) / 2 - (height ?? 0) / 2;
      const newPositionY =
        positionAbsoluteY + (height ?? 0) / 2 - (width ?? 0) / 2;

      setNodes((nds) =>
        nds.map((node) =>
          node.id === id
            ? {
                ...node,
                width: node.height,
                height: node.width,
                position: {
                  x: newPositionX,
                  y: newPositionY,
                },
                data: {
                  ...node.data,
                  category: {
                    ...(node.data?.category as IImageNodeCategory),
                    imageUrl: newImageUrl,
                  },
                },
              }
            : node,
        ),
      );
      updateNodeInternals(id);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('handleRotate ~ error:', error);
    } finally {
      setIsRotating(false);
    }
  };

  const handleMirror = async (horizontal = true) => {
    takeSnapshot();
    try {
      setIsRotating(true);
      const newImageUrl = category.imageUrl
        ? await mirrorBase64Image(category.imageUrl, horizontal)
        : '';
      setNodes((nds) =>
        nds.map((node) =>
          node.id === id
            ? {
                ...node,
                data: {
                  ...node.data,
                  category: {
                    ...(node.data?.category as IImageNodeCategory),
                    imageUrl: newImageUrl,
                  },
                },
              }
            : node,
        ),
      );
      updateNodeInternals(id);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('handleMirror ~ error:', error);
    } finally {
      setIsRotating(false);
    }
  };

  const onResizeStart: OnResizeStart = useCallback(
    (event, node) => {
      takeSnapshot();
    },
    [takeSnapshot],
  );

  return (
    <>
      <NodeResizer
        minWidth={48}
        minHeight={48}
        lineStyle={lineStyle}
        keepAspectRatio
        onResizeStart={onResizeStart}
      />
      <div className={twMerge('image-node')}>
        <NodeToolbar className="nodrag" position={Position.Bottom}>
          <div className="flex w-full items-center justify-center gap-2">
            <Button
              data-tooltip-id="app-tooltip"
              data-tooltip-content="Upload"
              color="green"
              disabled={isRotating}
              onClick={() => inputRef.current?.click()}
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <CloudArrowUpIcon className="h-4 w-4" />
            </Button>
            <Button
              disabled={isRotating}
              data-tooltip-id="app-tooltip"
              data-tooltip-content="Delete"
              color="red"
              onClick={onDelete}
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
            {category.imageUrl && (
              <>
                <Button
                  data-tooltip-id="app-tooltip"
                  data-tooltip-content="Rotate"
                  color="green"
                  disabled={isRotating}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRotate();
                  }}
                  onDoubleClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <RotateRightIcon className="h-4 w-4" />
                </Button>
                <Button
                  data-tooltip-id="app-tooltip"
                  data-tooltip-content="Mirror Horizontal"
                  color="indigo"
                  disabled={isRotating}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleMirror(true);
                  }}
                  onDoubleClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <FlipHIcon className="h-4 w-4" />
                </Button>
                <Button
                  data-tooltip-id="app-tooltip"
                  data-tooltip-content="Mirror Vertical"
                  color="indigo"
                  disabled={isRotating}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleMirror(false);
                  }}
                >
                  <FlipVIcon className="h-4 w-4" />
                </Button>
              </>
            )}
            {hasParent && (
              <Button
                data-tooltip-id="app-tooltip"
                data-tooltip-content="Detach"
                color="orange"
                onClick={() => onDetach()}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <ArrowLeftStartOnRectangleIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        </NodeToolbar>
        <div className="flex h-full w-full items-center justify-center overflow-hidden bg-gray-200">
          {category.imageUrl ? (
            <img
              src={category.imageUrl}
              alt=""
              className="h-full min-h-12 w-full min-w-12 object-contain"
            />
          ) : (
            <div
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                inputRef.current?.click();
              }}
              className="flex h-full w-full items-center justify-center"
            />
          )}
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            onChange={onUpload}
            accept="image/*"
          />
        </div>
      </div>
    </>
  );
}

export default memo(ImageNode);
